import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ImageUploader from '../components/ImageUploader';
import RichTextEditor from '../components/RichTextEditor';
import ShareButton from '../components/ShareButton';
import EditablePageIntro from '../components/EditablePageIntro';
import DisplayPrice from '../components/DisplayPrice';
import { resolveImageUrl } from '../utils/imageUrl';
import { Calendar, MapPin, Users, Clock, Star, Ticket, Plus, Info, ArrowRight, Loader2, Edit2, Trash2, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { formatDate, formatTime, utcIsoToLocalInput, localInputToUtcIso, browserTimezone } from '../utils/datetime';
import { CURRENCY_OPTIONS, currencySymbol, formatPrice } from '../utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EventsPage() {
    const { user, isAuthenticated } = useAuth();
    const [searchParams] = useSearchParams();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [bookingOpen, setBookingOpen] = useState(false);
    const [processing, setProcessing] = useState(false);
    
    // For non-members booking
    const [guestName, setGuestName] = useState('');
    const [guestEmail, setGuestEmail] = useState('');
    const [guestEmailConflict, setGuestEmailConflict] = useState(null); // {name} when the email already has an account
    // Free-event upsell: guest can optionally add the +€X 1-month Seeker
    // membership at checkout, routing what would have been a free RSVP through
    // Stripe. Price comes from site_settings.
    const [wantsSeekerUpsell, setWantsSeekerUpsell] = useState(false);
    const [seekerUpsellPrice, setSeekerUpsellPrice] = useState(1);
    
    // Add/Edit event form
    const [addEventOpen, setAddEventOpen] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [newEvent, setNewEvent] = useState({
        title: '',
        description: '',
        date: '',
        location: '',
        country: '',
        city: '',
        is_online: false,
        price: '',
        member_price: '',
        currency: 'eur',
        is_special: false,
        pillars_only: false,
        max_attendees: 30,
        image_url: ''
    });
    const [submitting, setSubmitting] = useState(false);
    // Location filters — country + city dropdowns, decoupled from the free-text search.
    // Online events always bypass these filters (see visibleEvents below).
    const [countryFilter, setCountryFilter] = useState('');
    const [cityFilter, setCityFilter] = useState('');
    // Attendee modal state — organizers/admins open this to see who booked their event
    const [attendeeModal, setAttendeeModal] = useState({ open: false, event: null, list: [], loading: false });

    // Check if user can manage events (admin or partnership)
    const canManageEvents = user?.role === 'admin' || user?.membership_tier === 'partnership';

    useEffect(() => {
        fetchEvents();
        // Pull upsell price from site settings (public endpoint)
        axios.get(`${API_URL}/api/site-settings`).then(({ data }) => {
            const p = Number(data?.seeker_upsell_price_eur);
            if (Number.isFinite(p) && p > 0) setSeekerUpsellPrice(p);
        }).catch(() => {});

        // Check for booking success
        const bookingSuccess = searchParams.get('booking_success');
        const sessionId = searchParams.get('session_id');
        if (bookingSuccess === 'true' && sessionId) {
            pollBookingStatus(sessionId);
        }
    }, [searchParams]);

    // Auto-open an event details modal via /events?open=<id> — used by Share
    // deep-links so newsletter / social clicks land directly on the event.
    useEffect(() => {
        const openId = searchParams.get('open');
        if (!openId || !events.length) return;
        const target = events.find(e => e.id === openId);
        if (target) {
            setSelectedEvent(target);
            setDetailsOpen(true);
        }
    }, [events, searchParams]);

    const pollBookingStatus = async (sessionId, attempts = 0) => {
        const maxAttempts = 8;
        const pollInterval = 2000;

        if (attempts >= maxAttempts) {
            toast.info('Payment is being processed. Please check your email for confirmation.');
            window.history.replaceState({}, document.title, '/events');
            return;
        }

        try {
            const { data } = await axios.get(`${API_URL}/api/events/booking/status/${sessionId}`, { withCredentials: true });
            
            if (data.payment_status === 'paid') {
                toast.success('You are registered! See you there.');
                window.history.replaceState({}, document.title, '/events');
                await fetchEvents();
                // Jump the user straight to their event list so they see the confirmation.
                setEventsTab('mine');
                return;
            } else if (data.status === 'expired') {
                toast.error('Payment session expired. Please try again.');
                window.history.replaceState({}, document.title, '/events');
                return;
            }

            setTimeout(() => pollBookingStatus(sessionId, attempts + 1), pollInterval);
        } catch (error) {
            console.error('Error checking booking status:', error);
            setTimeout(() => pollBookingStatus(sessionId, attempts + 1), pollInterval);
        }
    };

    const fetchEvents = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/events`, { withCredentials: true });
            setEvents(data);
        } catch (error) {
            console.error('Error fetching events:', error);
            toast.error('Failed to load events');
        } finally {
            setLoading(false);
        }
    };

    // On email blur, quietly check whether the address is already registered so
    // we can nudge the user to sign in instead of duplicating an account.
    const checkGuestEmail = async () => {
        const e = (guestEmail || '').trim().toLowerCase();
        if (!e || isAuthenticated) return;
        try {
            const { data } = await axios.get(`${API_URL}/api/auth/check-email`, { params: { email: e } });
            setGuestEmailConflict(data.exists ? { name: data.name } : null);
        } catch (_err) {
            setGuestEmailConflict(null);
        }
    };

    const handleBookEvent = async () => {
        if (!selectedEvent) return;
        
        if (!isAuthenticated && !guestEmail) {
            toast.error('Please enter your email address');
            return;
        }
        if (!isAuthenticated && guestEmailConflict) {
            toast.error('An account already exists — please sign in first.');
            return;
        }

        setProcessing(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/events/${selectedEvent.id}/checkout`,
                {
                    event_id: selectedEvent.id,
                    origin_url: window.location.origin,
                    name: guestName || undefined,
                    email: guestEmail || undefined,
                    wants_seeker_upsell: !isAuthenticated && wantsSeekerUpsell,
                },
                { withCredentials: true }
            );

            // Free event → booked directly, no Stripe redirect.
            if (data.free) {
                if (data.already_booked) {
                    toast.success('You are already registered for this event.');
                } else if (data.guest) {
                    // Free RSVP as a guest — we emailed the info, no account created.
                    toast.success("You're registered! Check your inbox for the details.");
                } else {
                    toast.success('You are registered! See you there.');
                }
                setBookingOpen(false);
                setProcessing(false);
                setWantsSeekerUpsell(false);
                fetchEvents();
                return;
            }

            window.location.href = data.url;
        } catch (error) {
            console.error('Checkout error:', error);
            const detail = error.response?.data?.detail;
            // 409 = account exists → surface the sign-in prompt in the modal
            if (error.response?.status === 409 && typeof detail === 'object' && detail?.code === 'account_exists') {
                setGuestEmailConflict({ name: detail.name || '' });
                toast.error(detail.message || 'Please sign in first.');
            } else {
                toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Failed to start checkout. Please try again.');
            }
            setProcessing(false);
        }
    };

    const handleAddEvent = async (e) => {
        e.preventDefault();
        if (!newEvent.title || !newEvent.date || !newEvent.location) {
            toast.error('Please fill in all required fields');
            return;
        }
        setSubmitting(true);
        try {
            const eventData = {
                ...newEvent,
                // datetime-local input is naive local time → convert to UTC ISO
                // so the backend can store a canonical value the browser can
                // safely re-render in every visitor's timezone.
                date: localInputToUtcIso(newEvent.date),
                price: parseFloat(newEvent.price) || 0,
                member_price: parseFloat(newEvent.member_price) || 0,
                currency: newEvent.currency || 'eur',
                max_attendees: parseInt(newEvent.max_attendees) || 30
            };

            if (editingEvent) {
                await axios.put(`${API_URL}/api/events/${editingEvent.id}`, eventData, { withCredentials: true });
                toast.success('Event updated successfully!');
            } else {
                await axios.post(`${API_URL}/api/events`, eventData, { withCredentials: true });
                toast.success('Event created successfully!');
            }
            
            setAddEventOpen(false);
            setEditingEvent(null);
            resetForm();
            fetchEvents();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save event');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteEvent = async (eventId) => {
        if (!window.confirm('Are you sure you want to delete this event? This will also delete all bookings.')) return;
        
        try {
            await axios.delete(`${API_URL}/api/events/${eventId}`, { withCredentials: true });
            toast.success('Event deleted');
            fetchEvents();
        } catch (error) {
            toast.error('Failed to delete event');
        }
    };

    const resetForm = () => {
        setNewEvent({
            title: '',
            description: '',
            date: '',
            location: '',
            country: '',
            city: '',
            is_online: false,
            price: '',
            member_price: '',
            currency: 'eur',
            is_special: false,
            pillars_only: false,
            max_attendees: 30,
            image_url: ''
        });
    };

    const openAttendees = async (event) => {
        setAttendeeModal({ open: true, event, list: [], loading: true });
        try {
            const { data } = await axios.get(`${API_URL}/api/events/${event.id}/attendees`, { withCredentials: true });
            setAttendeeModal({ open: true, event, list: data.attendees || [], loading: false });
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to load attendees');
            setAttendeeModal({ open: false, event: null, list: [], loading: false });
        }
    };

    const openEditDialog = (event) => {
        setEditingEvent(event);
        setNewEvent({
            title: event.title,
            description: event.description || '',
            date: event.date ? utcIsoToLocalInput(event.date) : '',
            location: event.location,
            country: event.country || '',
            city: event.city || '',
            is_online: !!event.is_online,
            price: event.price || '',
            member_price: event.member_price || '',
            currency: event.currency || 'eur',
            is_special: event.is_special || false,
            pillars_only: event.pillars_only || false,
            max_attendees: event.max_attendees || 30,
            image_url: event.image_url || ''
        });
        setAddEventOpen(true);
    };

    const getPrice = (event) => {
        if (user?.membership_tier) {
            return event.member_price;
        }
        return event.price;
    };

    const openEventDetails = (event) => {
        setSelectedEvent(event);
        setDetailsOpen(true);
    };

    const openBookingDialog = (event) => {
        setSelectedEvent(event);
        setGuestName('');
        setGuestEmail('');
        setBookingOpen(true);
    };

    // Tabs: upcoming / past / mine (registered)
    const [eventsTab, setEventsTab] = useState('upcoming');
    const now = Date.now();
    const filteredEvents = events.filter(ev => {
        const ts = ev.date ? new Date(ev.date).getTime() : 0;
        if (eventsTab === 'past') return ts < now;
        if (eventsTab === 'mine') return ev.booked;
        return ts >= now;
    }).filter(ev => {
        // Online events are ALWAYS visible regardless of location filters.
        if (ev.is_online) return true;
        if (countryFilter && (ev.country || '') !== countryFilter) return false;
        if (cityFilter && (ev.city || '') !== cityFilter) return false;
        return true;
    }).sort((a, b) => {
        const da = new Date(a.date).getTime();
        const dbT = new Date(b.date).getTime();
        return eventsTab === 'past' ? dbT - da : da - dbT;
    });

    // Build country/city options from the events currently in scope (respecting the tab).
    // City list narrows to whatever country is selected — better UX than one giant list.
    const { countryOptions, cityOptions } = (() => {
        const inTab = events.filter(ev => {
            if (ev.is_online) return false;
            const ts = ev.date ? new Date(ev.date).getTime() : 0;
            if (eventsTab === 'past') return ts < now;
            if (eventsTab === 'mine') return ev.booked;
            return ts >= now;
        });
        const countries = new Set(), cities = new Set();
        inTab.forEach(ev => {
            if (ev.country) countries.add(ev.country);
            if (ev.city && (!countryFilter || ev.country === countryFilter)) cities.add(ev.city);
        });
        return {
            countryOptions: Array.from(countries).sort(),
            cityOptions: Array.from(cities).sort()
        };
    })();

    const myEventsCount = events.filter(e => e.booked).length;

    return (
        <div className="min-h-screen bg-[#F9F6F0]">
            <Navbar />

            <main className="pt-24 pb-16" data-testid="events-page">
                <div className="max-w-7xl mx-auto px-6 sm:px-12">
                    {/* Header */}
                    <div className="text-center mb-16">
                        <p className="overline mb-4">Gatherings</p>
                        <h1 className="heading-2 mb-6">Upcoming Events</h1>
                        <EditablePageIntro
                            pageKey="events"
                            className="max-w-2xl mx-auto mb-8"
                            fallback={<p className="body-text">Join us for transformative experiences. Members enjoy special pricing, and Seeker membership is extended by 30 days with each event.</p>}
                        />
                        
                        {/* Add Event Button for Partnership/Admin */}
                        {canManageEvents && (
                            <Dialog open={addEventOpen} onOpenChange={(open) => {
                                setAddEventOpen(open);
                                if (!open) {
                                    setEditingEvent(null);
                                    resetForm();
                                }
                            }}>
                                <DialogTrigger asChild>
                                    <button className="btn-primary flex items-center gap-2 mx-auto" data-testid="add-event-btn">
                                        <Plus className="w-4 h-4" />
                                        Add Event
                                    </button>
                                </DialogTrigger>
                                <DialogContent className="sm:max-w-lg bg-white max-h-[90vh] overflow-y-auto">
                                    <DialogHeader>
                                        <DialogTitle className="font-serif text-2xl text-[#1A1A2E]">
                                            {editingEvent ? 'Edit Event' : 'Create New Event'}
                                        </DialogTitle>
                                    </DialogHeader>
                                    <form onSubmit={handleAddEvent} className="space-y-4 mt-4" data-testid="add-event-form">
                                        <div>
                                            <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Title *</label>
                                            <input
                                                type="text"
                                                value={newEvent.title}
                                                onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                                                className="w-full"
                                                required
                                                data-testid="event-title"
                                            />
                                        </div>
                                        <div>
                                            <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Description</label>
                                            <RichTextEditor
                                                value={newEvent.description}
                                                onChange={(html) => setNewEvent({ ...newEvent, description: html })}
                                                placeholder="Tell attendees what to expect. This shows on the event card AND in the confirmation email."
                                                data-testid="event-description"
                                            />
                                        </div>
                                        <div>
                                            <ImageUploader
                                                value={newEvent.image_url}
                                                onChange={(url) => setNewEvent({ ...newEvent, image_url: url })}
                                                testIdPrefix="event-image"
                                                label="Event Image"
                                                aspect="video"
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Date & Time *</label>
                                                <input
                                                    type="datetime-local"
                                                    value={newEvent.date}
                                                    onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                                                    className="w-full"
                                                    required
                                                    data-testid="event-date"
                                                />
                                                <p className="text-xs text-[#4A4A68] mt-1">
                                                    In your timezone (<strong>{browserTimezone()}</strong>). Visitors see this in their own local time.
                                                </p>
                                            </div>
                                            <div>
                                                <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Max Attendees</label>
                                                <input
                                                    type="number"
                                                    value={newEvent.max_attendees}
                                                    onChange={(e) => setNewEvent({ ...newEvent, max_attendees: e.target.value })}
                                                    className="w-full"
                                                    min="1"
                                                    data-testid="event-attendees"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Location * (venue / address, or "Online")</label>
                                            <input
                                                type="text"
                                                value={newEvent.location}
                                                onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                                                className="w-full"
                                                required
                                                data-testid="event-location"
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Country</label>
                                                <input
                                                    type="text"
                                                    value={newEvent.country}
                                                    onChange={(e) => setNewEvent({ ...newEvent, country: e.target.value })}
                                                    className="w-full"
                                                    placeholder="France"
                                                    data-testid="event-country"
                                                    disabled={newEvent.is_online}
                                                />
                                            </div>
                                            <div>
                                                <label className="block font-sans text-sm text-[#1A1A2E] mb-2">City</label>
                                                <input
                                                    type="text"
                                                    value={newEvent.city}
                                                    onChange={(e) => setNewEvent({ ...newEvent, city: e.target.value })}
                                                    className="w-full"
                                                    placeholder="Paris"
                                                    data-testid="event-city"
                                                    disabled={newEvent.is_online}
                                                />
                                            </div>
                                        </div>
                                        <label className="flex items-start gap-3 p-3 bg-[#F9F6F0] rounded-sm cursor-pointer" data-testid="event-online-label">
                                            <input
                                                type="checkbox"
                                                checked={newEvent.is_online}
                                                onChange={(e) => setNewEvent({ ...newEvent, is_online: e.target.checked })}
                                                className="mt-1"
                                                data-testid="event-online"
                                            />
                                            <div className="flex-1">
                                                <p className="text-sm text-[#1A1A2E]"><strong>This is an online event</strong></p>
                                                <p className="text-xs text-[#4A4A68] mt-1">Online events appear in every country's listing and ignore the location filter.</p>
                                            </div>
                                        </label>
                                        <div>
                                            <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Currency</label>
                                            <select
                                                value={newEvent.currency}
                                                onChange={(e) => setNewEvent({ ...newEvent, currency: e.target.value })}
                                                className="w-full"
                                                data-testid="event-currency"
                                            >
                                                {CURRENCY_OPTIONS.map(c => (
                                                    <option key={c.value} value={c.value}>{c.label}</option>
                                                ))}
                                            </select>
                                            <p className="text-xs text-[#4A4A68] mt-1">This is the currency Stripe will charge in. Visitors see your prices in this currency.</p>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Regular Price ({currencySymbol(newEvent.currency)})</label>
                                                <input
                                                    type="number"
                                                    value={newEvent.price}
                                                    onChange={(e) => setNewEvent({ ...newEvent, price: e.target.value })}
                                                    className="w-full"
                                                    min="0"
                                                    step="0.01"
                                                    data-testid="event-price"
                                                />
                                            </div>
                                            <div>
                                                <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Member Price ({currencySymbol(newEvent.currency)})</label>
                                                <input
                                                    type="number"
                                                    value={newEvent.member_price}
                                                    onChange={(e) => setNewEvent({ ...newEvent, member_price: e.target.value })}
                                                    className="w-full"
                                                    min="0"
                                                    step="0.01"
                                                    data-testid="event-member-price"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label htmlFor="event_access" className="block font-sans text-sm text-[#1A1A2E] mb-2">Who can book</label>
                                            <select
                                                id="event_access"
                                                value={newEvent.pillars_only ? 'pillars' : newEvent.is_special ? 'architects' : 'everyone'}
                                                onChange={(e) => setNewEvent({
                                                    ...newEvent,
                                                    is_special: e.target.value === 'architects',
                                                    pillars_only: e.target.value === 'pillars',
                                                })}
                                                className="w-full bg-white border border-[#E5E0D8] px-3 py-2 rounded-sm text-sm"
                                                data-testid="event-access-select"
                                            >
                                                <option value="everyone">Everyone</option>
                                                <option value="architects">Architects &amp; Pillars (Special Event)</option>
                                                <option value="pillars">Pillars only (Partnership)</option>
                                            </select>
                                        </div>
                                        <button
                                            type="submit"
                                            className="btn-primary w-full"
                                            disabled={submitting}
                                            data-testid="submit-event-btn"
                                        >
                                            {submitting ? 'Saving...' : (editingEvent ? 'Update Event' : 'Create Event')}
                                        </button>
                                    </form>
                                </DialogContent>
                            </Dialog>
                        )}
                    </div>

                    {/* Tabs */}
                    <div className="flex justify-center gap-2 mb-8 flex-wrap" data-testid="events-tabs">
                        <button
                            onClick={() => setEventsTab('upcoming')}
                            className={`px-6 py-2 font-sans text-sm uppercase tracking-widest border transition ${eventsTab === 'upcoming' ? 'bg-[#5B2D8E] text-white border-[#5B2D8E]' : 'bg-transparent text-[#1A1A2E] border-[#E5E0D8] hover:border-[#5B2D8E]'}`}
                            data-testid="events-tab-upcoming"
                        >
                            Upcoming
                        </button>
                        {isAuthenticated && (
                            <button
                                onClick={() => setEventsTab('mine')}
                                className={`px-6 py-2 font-sans text-sm uppercase tracking-widest border transition ${eventsTab === 'mine' ? 'bg-[#5B2D8E] text-white border-[#5B2D8E]' : 'bg-transparent text-[#1A1A2E] border-[#E5E0D8] hover:border-[#5B2D8E]'}`}
                                data-testid="events-tab-mine"
                            >
                                My events
                                {myEventsCount > 0 && (
                                    <span className={`ml-2 text-xs ${eventsTab === 'mine' ? 'text-white/80' : 'text-[#5B2D8E]'}`}>
                                        ({myEventsCount})
                                    </span>
                                )}
                            </button>
                        )}
                        <button
                            onClick={() => setEventsTab('past')}
                            className={`px-6 py-2 font-sans text-sm uppercase tracking-widest border transition ${eventsTab === 'past' ? 'bg-[#5B2D8E] text-white border-[#5B2D8E]' : 'bg-transparent text-[#1A1A2E] border-[#E5E0D8] hover:border-[#5B2D8E]'}`}
                            data-testid="events-tab-past"
                        >
                            Past
                        </button>
                    </div>

                    {/* Location filters — hidden on the 'mine' tab since it's already user-scoped */}
                    {eventsTab !== 'mine' && (countryOptions.length > 0 || cityOptions.length > 0) && (
                        <div className="flex flex-wrap items-center justify-center gap-3 mb-8" data-testid="events-location-filters">
                            <select
                                value={countryFilter}
                                onChange={(e) => { setCountryFilter(e.target.value); setCityFilter(''); }}
                                className="bg-white border border-[#E5E0D8] px-4 py-2 rounded-sm text-sm"
                                data-testid="events-filter-country"
                            >
                                <option value="">All countries</option>
                                {countryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                            <select
                                value={cityFilter}
                                onChange={(e) => setCityFilter(e.target.value)}
                                className="bg-white border border-[#E5E0D8] px-4 py-2 rounded-sm text-sm"
                                disabled={cityOptions.length === 0}
                                data-testid="events-filter-city"
                            >
                                <option value="">All cities</option>
                                {cityOptions.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                            {(countryFilter || cityFilter) && (
                                <button
                                    onClick={() => { setCountryFilter(''); setCityFilter(''); }}
                                    className="text-xs text-[#5B2D8E] hover:underline"
                                    data-testid="events-clear-location"
                                >
                                    Clear
                                </button>
                            )}
                            <p className="w-full text-center text-xs text-[#4A4A68] italic">
                                Online events are shown in every country.
                            </p>
                        </div>
                    )}

                    {/* Events Grid */}
                    {loading ? (
                        <div className="text-center py-12">
                            <div className="w-12 h-12 border-4 border-[#5B2D8E] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-[#4A4A68]">Loading events...</p>
                        </div>
                    ) : filteredEvents.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="events-grid">
                            {filteredEvents.map(event => (
                                <div
                                    key={event.id}
                                    className="bg-white border border-[#E5E0D8] rounded-sm overflow-hidden hover:shadow-lg transition-shadow"
                                    data-testid={`event-${event.id}`}
                                >
                                    {/* Event Image */}
                                    <div className="h-48 bg-gradient-to-br from-[#5B2D8E]/20 to-[#D8B4E2]/30 relative">
                                        {event.image_url ? (
                                            <img src={resolveImageUrl(event.image_url)} alt={event.title} className="w-full h-full object-cover" />
                                        ) : null}
                                        {(event.is_special || event.pillars_only) && (
                                            <div className="absolute top-4 right-4 flex items-center gap-1 bg-[#D4AF37] text-[#1A1A2E] px-3 py-1 rounded-sm text-xs font-medium" data-testid={`event-access-badge-${event.id}`}>
                                                <Star className="w-3 h-3" />
                                                {event.pillars_only ? 'Pillars only' : 'Special Event'}
                                            </div>
                                        )}
                                        {/* Edit/Delete buttons — admins see them on any event, Partnership only on their own */}
                                        {canManageEvents && (user?.role === 'admin' || String(event.created_by) === String(user?.id)) && (
                                            <div className="absolute top-4 left-4 flex gap-2">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openEditDialog(event);
                                                    }}
                                                    className="p-2 bg-white/90 rounded-sm hover:bg-white"
                                                    data-testid={`edit-event-${event.id}`}
                                                    title="Edit"
                                                >
                                                    <Edit2 className="w-4 h-4 text-[#5B2D8E]" />
                                                </button>
                                                <button
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        try {
                                                            await axios.post(`${API_URL}/api/events/${event.id}/copy`, {}, { withCredentials: true });
                                                            toast.success('Event duplicated');
                                                            fetchEvents();
                                                        } catch (err) {
                                                            toast.error('Copy failed');
                                                        }
                                                    }}
                                                    className="p-2 bg-white/90 rounded-sm hover:bg-white"
                                                    data-testid={`copy-event-${event.id}`}
                                                    title="Duplicate"
                                                >
                                                    <svg className="w-4 h-4 text-[#5B2D8E]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2v-2m-6-12h6a2 2 0 012 2v8a2 2 0 01-2 2H10a2 2 0 01-2-2V7a2 2 0 012-2z" /></svg>
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openAttendees(event);
                                                    }}
                                                    className="p-2 bg-white/90 rounded-sm hover:bg-white"
                                                    data-testid={`attendees-event-${event.id}`}
                                                    title="Attendees"
                                                >
                                                    <Users className="w-4 h-4 text-[#5B2D8E]" />
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteEvent(event.id);
                                                    }}
                                                    className="p-2 bg-white/90 rounded-sm hover:bg-white"
                                                    data-testid={`delete-event-${event.id}`}
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4 text-red-500" />
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <div className="p-6">
                                        <h3 className="font-serif text-xl text-[#1A1A2E] mb-1">
                                            {event.title}
                                        </h3>
                                        {event.creator_name && (
                                            <p className="text-xs text-[#4A4A68] mb-3" data-testid={`event-creator-${event.id}`}>
                                                by{' '}
                                                {event.creator_is_partnership && event.creator_id ? (
                                                    <Link
                                                        to={`/pillars/${event.creator_id}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="text-[#5B2D8E] hover:underline"
                                                    >
                                                        {event.creator_name}
                                                    </Link>
                                                ) : (
                                                    <span>{event.creator_name}</span>
                                                )}
                                            </p>
                                        )}

                                        <div
                                            className="font-sans text-sm text-[#4A4A68] mb-4 line-clamp-2 [&_p]:m-0 [&_ul]:list-disc [&_ul]:pl-4 [&_a]:text-[#5B2D8E]"
                                            dangerouslySetInnerHTML={{ __html: event.description || '' }}
                                        />

                                        <div className="space-y-2 mb-6">
                                            <div className="flex items-center gap-2 text-sm text-[#4A4A68]">
                                                <Calendar className="w-4 h-4 text-[#5B2D8E]" />
                                                {formatDate(event.date)}
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-[#4A4A68]">
                                                <Clock className="w-4 h-4 text-[#5B2D8E]" />
                                                {formatTime(event.date)}
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-[#4A4A68]">
                                                <MapPin className="w-4 h-4 text-[#5B2D8E]" />
                                                <span>
                                                    {event.is_online ? (
                                                        <span className="inline-flex items-center gap-1">
                                                            Online
                                                            <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-[#5B2D8E]/10 text-[#5B2D8E] rounded-sm">Live</span>
                                                        </span>
                                                    ) : (
                                                        <>
                                                            {event.location}
                                                            {(event.city || event.country) && (
                                                                <span className="text-[#4A4A68]/70"> · {[event.city, event.country].filter(Boolean).join(', ')}</span>
                                                            )}
                                                        </>
                                                    )}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Pricing */}
                                        <div className="mb-6 p-4 bg-[#F9F6F0] rounded-sm">
                                            <div className="flex items-baseline justify-between">
                                                <div>
                                                    <DisplayPrice
                                                        amount={getPrice(event)}
                                                        currency={event.currency}
                                                        className="font-serif text-2xl text-[#1A1A2E]"
                                                    />
                                                    {user?.membership_tier && event.member_price < event.price && (
                                                        <p className="text-xs text-[#5B2D8E] mt-1">Member price</p>
                                                    )}
                                                </div>
                                                {!user?.membership_tier && event.member_price < event.price && (
                                                    <div className="text-right">
                                                        <DisplayPrice amount={event.price} currency={event.currency} className="text-xs text-[#4A4A68] line-through" showNote={false} />
                                                        <DisplayPrice amount={event.member_price} currency={event.currency} className="text-xs text-[#5B2D8E] block" showNote={false} />
                                                        <span className="text-xs text-[#5B2D8E]"> for members</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => openEventDetails(event)}
                                                className="flex-1 btn-secondary text-xs px-4 py-3 flex items-center justify-center gap-2"
                                                data-testid={`event-details-${event.id}`}
                                            >
                                                <Info className="w-4 h-4" />
                                                More Info
                                            </button>
                                            {eventsTab !== 'past' && (
                                                event.booked ? (
                                                    <button
                                                        disabled
                                                        className="flex-1 bg-[#E5E0D8] text-[#4A4A68] rounded-sm font-sans text-xs uppercase tracking-widest px-4 py-3 flex items-center justify-center gap-2"
                                                        data-testid={`event-registered-${event.id}`}
                                                    >
                                                        <Check className="w-4 h-4" />
                                                        Registered
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => {
                                                            // For restricted events, route the user through the details
                                                            // modal so they see the "Sign in / Upgrade" options.
                                                            if (event.restricted) {
                                                                setSelectedEvent(event);
                                                                setDetailsOpen(true);
                                                            } else {
                                                                openBookingDialog(event);
                                                            }
                                                        }}
                                                        className="flex-1 btn-primary text-xs px-4 py-3"
                                                        data-testid={`book-event-${event.id}`}
                                                    >
                                                        {event.restricted ? (isAuthenticated ? (event.pillars_only ? 'Pillars only' : 'Upgrade to book') : 'Sign in to book') : 'Book Now'}
                                                    </button>
                                                )
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-16 bg-white border border-[#E5E0D8] rounded-sm" data-testid="no-events">
                            <Calendar className="w-12 h-12 text-[#D8B4E2] mx-auto mb-4" />
                            <h3 className="font-serif text-xl text-[#1A1A2E] mb-2">No upcoming events</h3>
                            <p className="text-[#4A4A68]">Check back soon for new gatherings.</p>
                        </div>
                    )}
                </div>
            </main>

            {/* Event Details Dialog */}
            <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
                <DialogContent className="sm:max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
                    {selectedEvent && (
                        <>
                            <DialogHeader>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            {(selectedEvent.is_special || selectedEvent.pillars_only) && (
                                                <span className="flex items-center gap-1 bg-[#D4AF37] text-[#1A1A2E] px-3 py-1 rounded-sm text-xs font-medium">
                                                    <Star className="w-3 h-3" />
                                                    {selectedEvent.pillars_only ? 'Pillars only' : 'Special Event'}
                                                </span>
                                            )}
                                        </div>
                                        <DialogTitle className="font-serif text-3xl text-[#1A1A2E]">
                                            {selectedEvent.title}
                                        </DialogTitle>
                                    </div>
                                    <ShareButton
                                        url={`${window.location.origin}/events?open=${selectedEvent.id}`}
                                        title={selectedEvent.title}
                                        text={`Join "${selectedEvent.title}" on Methode 4 Life`}
                                        size="sm"
                                        variant="outline"
                                        className="shrink-0 mr-6"
                                        testIdPrefix={`event-header-share-${selectedEvent.id}`}
                                    />
                                </div>
                            </DialogHeader>
                            
                            <div className="mt-6 space-y-6">
                                {/* Event Image */}
                                {selectedEvent.image_url && (
                                    <img 
                                        src={resolveImageUrl(selectedEvent.image_url)} 
                                        alt={selectedEvent.title}
                                        className="w-full h-64 object-cover rounded-sm"
                                    />
                                )}

                                {/* Date, Time, Location */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="flex items-center gap-3 p-4 bg-[#F9F6F0] rounded-sm">
                                        <Calendar className="w-6 h-6 text-[#5B2D8E]" />
                                        <div>
                                            <p className="text-xs text-[#4A4A68] uppercase tracking-wider">Date</p>
                                            <p className="font-sans text-[#1A1A2E]">{formatDate(selectedEvent.date)}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 p-4 bg-[#F9F6F0] rounded-sm">
                                        <Clock className="w-6 h-6 text-[#5B2D8E]" />
                                        <div>
                                            <p className="text-xs text-[#4A4A68] uppercase tracking-wider">Time</p>
                                            <p className="font-sans text-[#1A1A2E]">{formatTime(selectedEvent.date)}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 p-4 bg-[#F9F6F0] rounded-sm">
                                        <MapPin className="w-6 h-6 text-[#5B2D8E]" />
                                        <div>
                                            <p className="text-xs text-[#4A4A68] uppercase tracking-wider">Location</p>
                                            <p className="font-sans text-[#1A1A2E]">{selectedEvent.location}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Description */}
                                <div>
                                    <h4 className="font-serif text-xl text-[#1A1A2E] mb-3">About This Event</h4>
                                    <div
                                        className="font-sans text-[#4A4A68] leading-relaxed [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-[#5B2D8E] [&_strong]:text-[#1A1A2E]"
                                        dangerouslySetInnerHTML={{ __html: selectedEvent.description || '' }}
                                    />
                                </div>

                                {/* Spots Available */}
                                <div className="flex items-center gap-2 text-[#4A4A68]">
                                    <Users className="w-5 h-5 text-[#5B2D8E]" />
                                    <span>{selectedEvent.max_attendees - (selectedEvent.current_attendees || 0)} spots remaining</span>
                                </div>

                                {/* Pricing Section */}
                                <div className="bg-[#F2EBE5] p-6 rounded-sm">
                                    <h4 className="font-serif text-xl text-[#1A1A2E] mb-4">Pricing</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="text-center p-4 bg-white rounded-sm">
                                            <p className="text-xs text-[#4A4A68] uppercase tracking-wider mb-1">Regular Price</p>
                                            <DisplayPrice amount={selectedEvent.price} currency={selectedEvent.currency} className="font-serif text-3xl text-[#1A1A2E]" />
                                        </div>
                                        <div className="text-center p-4 bg-[#5B2D8E] text-white rounded-sm">
                                            <p className="text-xs uppercase tracking-wider mb-1 text-[#D8B4E2]">Member Price</p>
                                            <DisplayPrice amount={selectedEvent.member_price} currency={selectedEvent.currency} className="font-serif text-3xl" noteClassName="text-[#D8B4E2]" />
                                        </div>
                                    </div>
                                    {!user?.membership_tier && (
                                        <p className="text-center text-sm text-[#5B2D8E] mt-4">
                                            <Ticket className="w-4 h-4 inline mr-1" />
                                            Become a member to enjoy discounted pricing!
                                        </p>
                                    )}
                                </div>

                                {/* Book Button */}
                                {selectedEvent.restricted && !isAuthenticated ? (
                                    // Restricted events (e.g. Architect-only) — a member with the right tier
                                    // might already have an account, so we offer sign-in instead of a dead button.
                                    <div className="space-y-3" data-testid="restricted-signin-block">
                                        <div className="p-4 bg-[#5B2D8E]/10 border border-[#5B2D8E]/30 rounded-sm text-sm text-[#1A1A2E]">
                                            <p className="font-medium mb-1">{selectedEvent.pillars_only ? 'This event is reserved for Pillars (Partnership members).' : `This event is reserved for ${selectedEvent.min_tier || 'Architect'} members.`}</p>
                                            <p className="text-[#4A4A68]">{selectedEvent.pillars_only ? 'If you are a Pillar, sign in to book.' : 'If you already have a membership, sign in to book. Otherwise, join at that tier to unlock this event.'}</p>
                                        </div>
                                        <div className="flex flex-col sm:flex-row gap-3">
                                            <Link
                                                to={`/login?next=${encodeURIComponent(window.location.pathname)}`}
                                                className="btn-primary flex-1 text-center"
                                                data-testid="restricted-signin-btn"
                                            >
                                                Sign in
                                            </Link>
                                            <Link
                                                to="/membership"
                                                className="btn-secondary flex-1 text-center"
                                                data-testid="restricted-upgrade-btn"
                                            >
                                                View memberships
                                            </Link>
                                        </div>
                                    </div>
                                ) : selectedEvent.restricted ? (
                                    // Logged-in but insufficient tier — offer the upgrade path.
                                    <div className="space-y-3" data-testid="restricted-upgrade-block">
                                        <div className="p-4 bg-[#5B2D8E]/10 border border-[#5B2D8E]/30 rounded-sm text-sm text-[#1A1A2E]">
                                            <p className="font-medium mb-1">{selectedEvent.pillars_only ? 'Reserved for Pillars (Partnership members).' : `Reserved for ${selectedEvent.min_tier || 'Architect'} members.`}</p>
                                            <p className="text-[#4A4A68]">{selectedEvent.pillars_only ? 'This event is only open to Pillars.' : 'Upgrade your membership to book this event.'}</p>
                                        </div>
                                        <Link to="/membership" className="btn-primary w-full text-center block" data-testid="restricted-upgrade-cta">
                                            View memberships
                                        </Link>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => {
                                            setDetailsOpen(false);
                                            openBookingDialog(selectedEvent);
                                        }}
                                        className="btn-primary w-full flex items-center justify-center gap-2"
                                        data-testid="book-from-details"
                                    >
                                        Book This Event
                                        <ArrowRight className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Booking Confirmation Dialog */}
            <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
                <DialogContent className="sm:max-w-md bg-white">
                    {selectedEvent && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-serif text-2xl text-[#1A1A2E]">
                                    Complete Your Booking
                                </DialogTitle>
                            </DialogHeader>
                            
                            <div className="mt-4 space-y-6">
                                <div>
                                    <h4 className="font-serif text-xl text-[#1A1A2E] mb-2">
                                        {selectedEvent.title}
                                    </h4>
                                    <p className="text-sm text-[#4A4A68]">
                                        {formatDate(selectedEvent.date)} at {formatTime(selectedEvent.date)}
                                    </p>
                                    <p className="text-sm text-[#4A4A68]">{selectedEvent.location}</p>
                                </div>

                                {/* Guest Info (for non-members) */}
                                {!isAuthenticated && (
                                    <div className="space-y-4 p-4 bg-[#F9F6F0] rounded-sm">
                                        <div className="flex items-center justify-between">
                                            <p className="text-sm text-[#5B2D8E] font-medium">Your Information</p>
                                            <Link
                                                to={`/login?next=${encodeURIComponent(window.location.pathname)}`}
                                                className="text-xs text-[#5B2D8E] hover:underline"
                                                data-testid="event-modal-signin-link"
                                            >
                                                Already a client? Sign in →
                                            </Link>
                                        </div>
                                        <div>
                                            <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Name</label>
                                            <input
                                                type="text"
                                                value={guestName}
                                                onChange={(e) => setGuestName(e.target.value)}
                                                className="w-full"
                                                placeholder="Your name"
                                                data-testid="guest-name"
                                            />
                                        </div>
                                        <div>
                                            <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Email *</label>
                                            <input
                                                type="email"
                                                value={guestEmail}
                                                onChange={(e) => { setGuestEmail(e.target.value); setGuestEmailConflict(null); }}
                                                onBlur={checkGuestEmail}
                                                className="w-full"
                                                placeholder="your@email.com"
                                                required
                                                data-testid="guest-email"
                                            />
                                            {guestEmailConflict && (
                                                <div className="mt-2 p-3 bg-[#5B2D8E]/10 border border-[#5B2D8E]/30 rounded-sm text-sm text-[#1A1A2E]" data-testid="guest-email-conflict">
                                                    <strong>An account with this email already exists.</strong>{' '}
                                                    <Link
                                                        to={`/login?next=${encodeURIComponent(window.location.pathname)}`}
                                                        className="text-[#5B2D8E] hover:underline"
                                                    >
                                                        Sign in
                                                    </Link>{' '}
                                                    to continue, or{' '}
                                                    <Link
                                                        to="/forgot-password"
                                                        className="text-[#5B2D8E] hover:underline"
                                                    >
                                                        reset your password
                                                    </Link>.
                                                </div>
                                            )}
                                        </div>
                                        {/* Seeker upsell — only shown for FREE events. Paid events
                                            already auto-create a Seeker for the buyer on fulfillment. */}
                                        {getPrice(selectedEvent) <= 0 && seekerUpsellPrice > 0 && (
                                            <label className="flex items-start gap-3 p-3 bg-white border border-[#5B2D8E]/20 rounded-sm cursor-pointer" data-testid="event-seeker-upsell-label">
                                                <input
                                                    type="checkbox"
                                                    checked={wantsSeekerUpsell}
                                                    onChange={(e) => setWantsSeekerUpsell(e.target.checked)}
                                                    className="mt-1"
                                                    data-testid="event-seeker-upsell-checkbox"
                                                />
                                                <div className="flex-1">
                                                    <p className="text-sm text-[#1A1A2E]">
                                                        <strong>Add {seekerUpsellPrice.toFixed(2)}€ to unlock a 1-month Seeker membership.</strong>
                                                    </p>
                                                    <p className="text-xs text-[#4A4A68] mt-1">
                                                        Optional. Otherwise, you'll just receive the event info by email — no account created.
                                                    </p>
                                                </div>
                                            </label>
                                        )}
                                        <p className="text-xs text-[#4A4A68]">
                                            {getPrice(selectedEvent) > 0
                                                ? "By booking, you'll automatically receive a Seeker membership for 30 days."
                                                : "We'll email you the event details. Check your spam folder if it doesn't arrive within a few minutes."}
                                        </p>
                                    </div>
                                )}

                                {/* Price Summary */}
                                <div className="bg-[#F2EBE5] p-4 rounded-sm">
                                    <div className="flex justify-between items-center">
                                        <span className="text-[#4A4A68]">Total</span>
                                        <DisplayPrice amount={getPrice(selectedEvent)} currency={selectedEvent.currency} className="font-serif text-2xl text-[#1A1A2E] items-end" />
                                    </div>
                                    {user?.membership_tier && selectedEvent.member_price < selectedEvent.price && (
                                        <p className="text-xs text-[#5B2D8E] mt-1">
                                            You save {formatPrice((selectedEvent.price - selectedEvent.member_price).toFixed(2), selectedEvent.currency)} with your membership!
                                        </p>
                                    )}
                                </div>

                                {user?.membership_tier === 'seeker' && (
                                    <div className="flex items-center gap-2 text-sm text-[#5B2D8E] bg-[#D8B4E2]/20 p-3 rounded-sm">
                                        <Ticket className="w-4 h-4" />
                                        Booking extends your membership by 30 days!
                                    </div>
                                )}

                                <button
                                    onClick={handleBookEvent}
                                    className="btn-primary w-full flex items-center justify-center gap-2"
                                    disabled={processing}
                                    data-testid="confirm-booking-btn"
                                >
                                    {processing ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            Proceed to Payment
                                            <ArrowRight className="w-4 h-4" />
                                        </>
                                    )}
                                </button>

                                <p className="text-xs text-center text-[#4A4A68]">
                                    You'll be redirected to our secure payment provider
                                </p>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Attendees modal — event creator / admin can copy the list of buyers
                (including guest RSVPs) to send them the meeting link or logistics. */}
            {attendeeModal.open && (
                <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={() => setAttendeeModal({ open: false, event: null, list: [], loading: false })} data-testid="attendee-modal">
                    <div className="bg-white rounded-sm max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="p-6 border-b border-[#E5E0D8] flex items-center justify-between">
                            <div>
                                <p className="overline">Attendees</p>
                                <h3 className="heading-3">{attendeeModal.event?.title}</h3>
                                <p className="text-sm text-[#4A4A68] mt-1">{attendeeModal.list.length} registered</p>
                            </div>
                            <button
                                onClick={() => setAttendeeModal({ open: false, event: null, list: [], loading: false })}
                                className="text-[#4A4A68] hover:text-[#5B2D8E] text-2xl"
                                aria-label="Close"
                                data-testid="close-attendee-modal"
                            >
                                ×
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6">
                            {attendeeModal.loading ? (
                                <p className="text-center text-[#4A4A68] py-8">Loading…</p>
                            ) : attendeeModal.list.length === 0 ? (
                                <p className="text-center text-[#4A4A68] py-8">No one has registered yet.</p>
                            ) : (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between mb-3">
                                        <p className="text-xs uppercase tracking-widest text-[#4A4A68]">All buyers</p>
                                        <button
                                            onClick={() => {
                                                const emails = attendeeModal.list.map(a => a.email).join(', ');
                                                navigator.clipboard?.writeText(emails);
                                                toast.success('Emails copied to clipboard');
                                            }}
                                            className="text-xs text-[#5B2D8E] hover:underline"
                                            data-testid="copy-all-emails"
                                        >
                                            Copy all emails
                                        </button>
                                    </div>
                                    {attendeeModal.list.map(a => (
                                        <div key={a.id} className="grid grid-cols-[1fr_auto] gap-3 items-center py-2 px-3 bg-[#F9F6F0] rounded-sm" data-testid={`attendee-row-${a.id}`}>
                                            <div>
                                                <p className="text-sm text-[#1A1A2E]"><strong>{a.name || '—'}</strong></p>
                                                <a href={`mailto:${a.email}`} className="text-xs text-[#5B2D8E] hover:underline">{a.email}</a>
                                            </div>
                                            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm ${a.guest ? 'bg-[#5B2D8E]/10 text-[#5B2D8E]' : 'bg-green-50 text-green-700'}`}>
                                                {a.guest ? 'Guest' : (a.membership_tier || 'Member')}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
}
